import { ratesByState } from '@/app/(marketplace)/quote-builder/electricity-rates/rates';

import {
  MONTHLY_FIXED_CHARGE_USD,
  solarAnnualKwhPerKwByRegion,
  SOLAR_EXPORT_RATE_FACTOR,
  SOLAR_MAX_KW,
  SOLAR_MIN_KW,
  SOLAR_SELF_CONSUMPTION_FRACTION,
  SOLAR_USD_PER_WATT,
  UNCERTAINTY_BAND,
  UNCERTAINTY_BAND_REFINED,
} from '../data/content';

import { scenarioEscalator } from './scenarios';
import { climateRegionByState } from './stateRegions';
import { BillPoint, ProjectionResult, SolarProjectionInput, YearPoint } from './types';

// A state is projectable only with both a climate region and an EIA rate row —
// the single source of truth for the state picker and the per-state tests
export const supportedStateCodes = Object.keys(climateRegionByState).filter((stateCode) => ratesByState[stateCode]);

const latestCentsPerKwh = (stateCode: string): number | undefined => {
  const { rates } = ratesByState[stateCode] ?? {};
  return rates?.[rates.length - 1]?.rate;
};

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

// Production is valued at retail for the self-consumed share and below retail
// for exports (post-NEM-3 national stance) — see data/content.ts TODOs.
const solarValueFactor =
  SOLAR_SELF_CONSUMPTION_FRACTION + (1 - SOLAR_SELF_CONSUMPTION_FRACTION) * SOLAR_EXPORT_RATE_FACTOR;

// Running totals over the chart's own projected bills. Savings track the bill
// they offset — a curve twice as high offsets twice as much — so the two lines
// stay tied to the plotted series instead of a second escalator of their own.
// Scaled against today's bill so the first projected step counts, and stopped
// at each year's variable charges so no band models solar erasing the fixed charge.
const walkBillPoints = (
  projected: BillPoint[],
  anchorMonthlyBillUsd: number,
  annualSavingsUsd: number,
  systemCostUsd: number
): YearPoint[] => {
  const [first] = projected;
  const points: YearPoint[] = [{ calendarYear: first.year - 1, doNothingUsd: 0, withSolarUsd: systemCostUsd, year: 0 }];

  projected.forEach(({ monthlyBillUsd }, i) => {
    const annualBillUsd = monthlyBillUsd * 12;
    const variableBillUsd = Math.max(annualBillUsd - MONTHLY_FIXED_CHARGE_USD * 12, 0);
    const savingsUsd = Math.min(annualSavingsUsd * (monthlyBillUsd / anchorMonthlyBillUsd), variableBillUsd);
    const previous = points[i];

    points.push({
      calendarYear: first.year + i,
      doNothingUsd: previous.doNothingUsd + annualBillUsd,
      withSolarUsd: previous.withSolarUsd + (annualBillUsd - savingsUsd),
      year: i + 1,
    });
  });

  return points;
};

/** First year offset where the running totals cross */
const crossover = (years: YearPoint[]): number | null =>
  years.find(({ doNothingUsd, withSolarUsd }, i) => i > 0 && withSolarUsd <= doNothingUsd)?.year ?? null;

// Interpolated to one decimal so the displayed range reads 10–14, not 10–15
const paybackYears = (years: YearPoint[]): number | null => {
  const crossed = crossover(years);
  if (crossed === null) {
    return null;
  }

  const before = years[crossed - 1];
  const after = years[crossed];
  const owedUsd = before.withSolarUsd - before.doNothingUsd;
  const closedUsd = owedUsd - (after.withSolarUsd - after.doNothingUsd);

  return Math.round((before.year + (closedUsd > 0 ? owedUsd / closedUsd : 0)) * 10) / 10;
};

export const runProjection = ({
  billPoints,
  homeSqft,
  scenario,
  stateCode,
}: SolarProjectionInput): ProjectionResult | undefined => {
  const latestCents = latestCentsPerKwh(stateCode);
  const region = climateRegionByState[stateCode];
  const projected = billPoints.filter(({ isProjected }) => isProjected);
  // The entered bill, from the one place that holds it — the last actual year
  const anchor = billPoints.filter(({ isProjected }) => !isProjected).at(-1);
  if (!latestCents || !region || !anchor || projected.length === 0) {
    return undefined;
  }

  // Today's bill already carries this year's step, so usage is priced at this
  // year's rate — last year's EIA average would oversize the system
  const centsPerKwh = latestCents * (1 + (anchor.yoyFraction ?? 0));
  const rateUsd = centsPerKwh / 100;
  const annualBillUsd = anchor.monthlyBillUsd * 12;
  const annualKwh = annualBillUsd / rateUsd;

  const kwhPerKw = solarAnnualKwhPerKwByRegion[region];
  const systemSizeKw = clamp(Math.round((annualKwh / kwhPerKw) * 2) / 2, SOLAR_MIN_KW, SOLAR_MAX_KW);
  const systemCostUsd = Math.round(systemSizeKw * 1000 * SOLAR_USD_PER_WATT);

  // Savings cannot exceed the variable portion of the bill
  const productionValueUsd = systemSizeKw * kwhPerKw * rateUsd * solarValueFactor;
  const annualSavingsUsd = Math.min(productionValueUsd, Math.max(annualBillUsd - MONTHLY_FIXED_CHARGE_USD * 12, 0));

  const band = homeSqft ? UNCERTAINTY_BAND_REFINED : UNCERTAINTY_BAND;
  const walkFor = (savingsUsd: number) => walkBillPoints(projected, anchor.monthlyBillUsd, savingsUsd, systemCostUsd);
  const years = walkFor(annualSavingsUsd);
  const low = walkFor(annualSavingsUsd * (1 - band));
  const high = walkFor(annualSavingsUsd * (1 + band));

  const difference = (walk: YearPoint[]): number => {
    const final = walk[walk.length - 1];
    return Math.max(final.doNothingUsd - final.withSolarUsd, 0);
  };

  const paybackLow = paybackYears(high);
  const paybackHigh = paybackYears(low);

  return {
    centsPerKwh,
    crossoverYear: crossover(years),
    escalationRate: scenarioEscalator(stateCode, scenario),
    // `paybackHigh` stays null when the cautious walk never crosses — the
    // horizon length is not a payback figure, so it must not stand in for one
    paybackYearsRange: paybackLow === null ? null : { high: paybackHigh, low: paybackLow },
    savingsRange: { high: difference(high), low: difference(low) },
    systemCostUsd,
    systemSizeKw,
    years,
  };
};
